import { Calendar, Info } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { Label, XStack, YStack } from "tamagui";
import { getDayJsFromDate } from "../../core/dayJs";
import { Permission } from "../../permissions/types";
import { usePermissionCheck } from "../../permissions/useCheckPermission";
import { Group } from "../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../ui/InfoRow";
import { Sheet, type SheetProps } from "../../ui/Sheet";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";
import { useDisplayNameInfo } from "../feed/utils";
import type { BusinessGenericModel } from "../modules/types";

export const BusinessModuleMembersSheet: React.FC<
  SheetProps & {
    businessModule: BusinessGenericModel;
  }
> = observer(({ businessModule, open, ...props }) => {
  const getDisplayInfo = useDisplayNameInfo();
  const { t } = useTranslation();

  const { checkPermission } = usePermissionCheck();
  const users = businessModule.completedUserInfo;

  return (
    <Sheet
      open={open}
      {...props}
      sheetId="b-module-users"
      header={
        <YStack gap="$space.small">
          <Title>Members</Title>
          <TipsText text="Progress in detail" />
        </YStack>
      }
    >
      <Group borderTop={false} borderBottom={false}>
        {users.map((userInfo) => {
          const infoRowItems: InfoRowItemProps[] = [];

          const displayInfo = getDisplayInfo(userInfo);

          if (displayInfo) {
            infoRowItems.push(displayInfo);
          }

          infoRowItems.push({
            icon: Calendar,
            text: getDayJsFromDate(new Date(userInfo.completedAt)),
          });
          if (userInfo.skipped) {
            infoRowItems.push({
              icon: Info,
              text: t("business:skipped_this"),
            });
          }

          return (
            <XStack
              key={userInfo.userUid}
              width={"100%"}
              gap={"$medium"}
              flexWrap={"wrap"}
            >
              <UserPropsAvatar
                {...userInfo}
                size={AvatarSize.Medium}
                showBorder={checkPermission(
                  Permission.AvatarShowExtraBorder,
                  userInfo.userUid,
                )}
              />
              <YStack gap={"$small"}>
                <Label>{userInfo.displayName}</Label>
                <InfoRow items={infoRowItems} />
              </YStack>
            </XStack>
          );
        })}
      </Group>
    </Sheet>
  );
});
